import { formatPartnerActivityDateTime } from './partnerDateUtils';

const GENERIC_ACTIVITY_NOTES = new Set([
  'Project approved.',
  'Project status updated.',
  'PAE action recorded.',
  'Additional information requested from partner.',
]);

const APPROVAL_ACTIONS = new Set(['approve', 'approved']);

const normalizeDisplayNote = (note) => {
  const trimmed = String(note || '').trim();
  if (!trimmed || GENERIC_ACTIVITY_NOTES.has(trimmed)) {
    return '';
  }
  return trimmed;
};

const getActivityNote = (item) => normalizeDisplayNote(
  item.psmNote || item.note || item.message || item.text || '',
);

const getActivityTimestamp = (item) => (
  item.performedAt || item.timestamp || item.createdAt || item.completedAt
);

const getActivityAuthor = (item) => (
  item.performedBy || item.author || item.createdBy || item.psmName || ''
);

const formatActionLabel = (action) => (
  String(action || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const normalizeChanges = (item) => {
  if (!Array.isArray(item.changes)) {
    return [];
  }
  return item.changes
    .filter((change) => change && change.label)
    .map((change) => ({
      field: change.field || change.label,
      label: change.label,
      from: change.from,
      to: change.to,
    }));
};

const getActivityKind = (action, item) => {
  if (action === 'submitted') {
    return 'submission';
  }
  if (action === 'project_updated') {
    return 'field_update';
  }
  if (['add_note', 'psm_note', 'note_added'].includes(action)) {
    return 'psm_note';
  }
  if (APPROVAL_ACTIONS.has(action)) {
    return getActivityNote(item) ? 'approved_with_note' : 'approved';
  }
  if (['info_requested', 'request_info', 'status_changed', 'update_status'].includes(action)) {
    return getActivityNote(item) ? 'status_with_note' : 'status';
  }
  return 'activity';
};

const getActivityBadge = (kind) => {
  switch (kind) {
    case 'submission':
      return 'Create';
    case 'field_update':
      return 'Update';
    case 'psm_note':
      return 'PAE note';
    case 'approved':
    case 'approved_with_note':
      return 'Status';
    case 'status':
    case 'status_with_note':
      return 'Status';
    default:
      return 'Activity';
  }
};

const getActivityTitle = (action, item) => {
  const status = item.projectStatus || '';

  switch (action) {
    case 'submitted':
      return 'Project created';
    case 'project_updated':
      return 'Project details updated';
    case 'approved':
    case 'approve':
      return 'Project approved';
    case 'info_requested':
    case 'request_info':
      return 'More information requested';
    case 'status_changed':
    case 'update_status':
      return status ? `Status updated to ${status}` : 'Project status updated';
    case 'note_added':
    case 'add_note':
    case 'psm_note':
      return 'PAE note added';
    default:
      return formatActionLabel(action);
  }
};

const getNoteLabel = (kind, action) => {
  if (kind === 'submission') {
    return 'Submission note';
  }
  if (['note_added', 'add_note', 'psm_note'].includes(action)) {
    return 'PAE note';
  }
  return 'PAE note';
};

const getActivitySummary = (action, item, project) => {
  const note = getActivityNote(item);

  if (action === 'project_updated') {
    const changeCount = normalizeChanges(item).length;
    if (changeCount > 0) {
      return `${changeCount} field${changeCount === 1 ? '' : 's'} updated`;
    }
    return '';
  }

  if (action === 'submitted') {
    return project?.partnerName ? `Partner: ${project.partnerName}` : '';
  }

  if (['note_added', 'add_note'].includes(action) && !normalizeChanges(item).length) {
    return note ? '' : 'PAE note recorded';
  }

  if (['approved', 'approve'].includes(action) && note && note !== 'Project approved.') {
    return '';
  }

  if (['info_requested', 'request_info'].includes(action) && note) {
    return 'Partner was asked to provide additional details';
  }

  if (!note && ['approved', 'approve'].includes(action)) {
    return '';
  }

  return '';
};

const dedupeApprovalActivityEntries = (entries) => {
  let latestApprovalIndex = -1;
  let latestApprovalTime = Number.NEGATIVE_INFINITY;

  entries.forEach((entry, index) => {
    if (!APPROVAL_ACTIONS.has(entry.action)) {
      return;
    }
    const time = new Date(entry.timestamp || 0).getTime();
    if (Number.isNaN(time)) {
      return;
    }
    if (time >= latestApprovalTime) {
      latestApprovalTime = time;
      latestApprovalIndex = index;
    }
  });

  if (latestApprovalIndex < 0) {
    return entries;
  }

  return entries.filter((entry, index) => {
    if (!APPROVAL_ACTIONS.has(entry.action)) {
      return true;
    }
    return index === latestApprovalIndex;
  });
};

const shouldAttachNote = (action, item, kind) => {
  const note = getActivityNote(item);
  if (!note) {
    return false;
  }
  if (kind === 'submission' || kind === 'psm_note' || kind === 'status_with_note' || kind === 'approved_with_note') {
    return true;
  }
  if (action === 'project_updated') {
    return false;
  }
  return false;
};

const buildLogEntry = ({
  id,
  kind,
  badge,
  title,
  summary,
  timestamp,
  author,
  action,
  changes,
  note,
  noteLabel,
}) => ({
  id,
  kind,
  badge,
  title,
  summary,
  timestamp,
  dateTime: formatPartnerActivityDateTime(timestamp),
  author,
  action,
  changes,
  note,
  noteLabel,
});

export const buildPSMActivityLog = (project) => {
  const entries = [];
  const remoteEntries = Array.isArray(project?.activityLog) ? project.activityLog : [];
  const hasSubmittedInLog = remoteEntries.some(
    (item) => String(item.action || '').toLowerCase() === 'submitted',
  );

  remoteEntries.forEach((item, index) => {
    const action = String(item.action || '').toLowerCase();

    if (action === 'received') {
      return;
    }

    const timestamp = getActivityTimestamp(item);
    const author = getActivityAuthor(item);
    const kind = getActivityKind(action, item);
    const note = getActivityNote(item);
    const changes = normalizeChanges(item);

    entries.push(buildLogEntry({
      id: item.id || `activity-${index}-${timestamp || index}`,
      kind,
      badge: getActivityBadge(kind),
      title: getActivityTitle(action, item),
      summary: getActivitySummary(action, item, project),
      timestamp,
      author,
      action,
      changes,
      note: shouldAttachNote(action, item, kind) ? note : '',
      noteLabel: getNoteLabel(kind, action),
    }));
  });

  if (project?.submittedAt && !hasSubmittedInLog) {
    const submitter = project.submittedBy || project.projectLeadName || project.partnerName || 'Partner';
    entries.push(buildLogEntry({
      id: `submitted-${project.id}`,
      kind: 'submission',
      badge: getActivityBadge('submission'),
      title: 'Project created',
      summary: `Submitted by ${submitter}${project.partnerName ? ` (${project.partnerName})` : ''}`,
      timestamp: project.submittedAt,
      author: submitter,
      action: 'submitted',
      changes: [],
      note: normalizeDisplayNote(project.additionalNotes || ''),
      noteLabel: 'Submission note',
    }));
  }

  const legacyNotes = [
    ...(Array.isArray(project?.psmNotes) ? project.psmNotes : []),
    ...(Array.isArray(project?.activities) ? project.activities : []),
  ];

  legacyNotes.forEach((item, index) => {
    const message = item.message || item.note || item.text || item.psmNote || '';
    if (!message) {
      return;
    }

    const timestamp = getActivityTimestamp(item);
    const author = getActivityAuthor(item);
    entries.push(buildLogEntry({
      id: item.id || `legacy-note-${index}`,
      kind: 'psm_note',
      badge: getActivityBadge('psm_note'),
      title: 'PAE note added',
      summary: '',
      timestamp,
      author,
      action: 'note_added',
      changes: [],
      note: message.startsWith('PAE note:') ? message.replace(/^PAE note:\s*/i, '') : message,
      noteLabel: 'PAE note',
    }));
  });

  return dedupeApprovalActivityEntries(entries.sort((left, right) => {
    const leftTime = new Date(left.timestamp || 0).getTime();
    const rightTime = new Date(right.timestamp || 0).getTime();
    if (leftTime !== rightTime) {
      return leftTime - rightTime;
    }
    if (left.action === 'submitted') {
      return -1;
    }
    if (right.action === 'submitted') {
      return 1;
    }
    return 0;
  }));
};
